import { useState } from 'react';
import { editSelfPayPayment, voidSelfPayPayment } from '../services/api';
import './SelfPayEditModal.css';

const PAYMENT_METHODS = [
  { value: 'cash',        label: 'Cash' },
  { value: 'check',       label: 'Check' },
  { value: 'card',        label: 'Credit Card' },
  { value: 'money_order', label: 'Money Order' },
];

const REF_LABELS = {
  check:       'Check Number',
  card:        'Auth / Approval Code',
  money_order: 'Money Order #',
  cash:        'Receipt #',
};

function fmt(n) {
  return '$' + (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Correct or void a one-time self-pay payment. `mode` is 'edit' | 'void'.
 * Both paths require a reason and record an append-only override event.
 */
export default function SelfPayEditModal({ mode, payment, onClose, onSaved }) {
  const isVoid = mode === 'void';

  // Both legs of an internal credit transfer carry this method. They are equal and
  // opposite and must stay that way, so the server refuses to edit either one and
  // voids the pair together — say so before the click, not after.
  const isTransfer = payment.payment_method === 'credit_transfer';

  const [form, setForm] = useState({
    payment_date:     (payment.payment_date || '').slice(0, 10),
    amount:           payment.amount != null ? String(parseFloat(payment.amount)) : '',
    payment_method:   payment.payment_method || 'check',
    reference_number: payment.reference_number || '',
    notes:            payment.notes || '',
  });
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  // Soft, overridable overpayment guard (edit path). Remaining balance excludes the
  // payment being edited (paid_excluding_this) so raising the amount is measured against
  // what the claim can still absorb. Warn — don't block — since overpayments are
  // legitimate and post as a refundable patient credit.
  const remainingExclThis = Math.max(
    0,
    (parseFloat(payment.effective_resp) || 0) - (parseFloat(payment.paid_excluding_this) || 0)
  );
  const overpayment = isVoid
    ? 0
    : Math.max(0, (parseFloat(form.amount) || 0) - remainingExclThis);

  const handleSubmit = async () => {
    setError(null);
    if (!reason.trim()) {
      setError(`A reason is required to ${isVoid ? 'void' : 'edit'} this payment.`);
      return;
    }
    if (!isVoid && (!form.amount || parseFloat(form.amount) <= 0)) {
      setError('Please enter a valid payment amount.');
      return;
    }
    setSaving(true);
    try {
      let message = null;
      if (isVoid) {
        const res = await voidSelfPayPayment(payment.patient_id, payment.patient_payment_id, reason.trim());
        message = res?.message || null;
      } else {
        await editSelfPayPayment(payment.patient_id, payment.patient_payment_id, {
          payment_date:     form.payment_date,
          amount:           parseFloat(form.amount),
          payment_method:   form.payment_method,
          reference_number: form.reference_number || null,
          notes:            form.notes || null,
          reason:           reason.trim(),
        });
      }
      // The server says what it actually did — for a transfer that is "both sides
      // voided", which is more than the row the user clicked on.
      onSaved(message);
    } catch (err) {
      setError(err?.response?.data?.error || `Failed to ${isVoid ? 'void' : 'edit'} payment.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="spe-overlay">
      <div className="spe-panel" role="dialog" aria-modal="true">
        <div className="spe-header">
          <div>
            <h2 className="spe-title">{isVoid ? 'Void Payment' : 'Edit Payment'}</h2>
            <p className="spe-sub">
              {payment.patient_name} · Claim {payment.claim_number} · {fmt(payment.amount)}
            </p>
          </div>
          <button className="spe-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="spe-body">
          {isVoid ? (
            <>
              <div className="spe-void-note">
                Voiding removes this payment from the patient&apos;s balance and all collection totals.
                The record stays visible (struck-through) with your reason for audit. This cannot be undone.
              </div>
              {isTransfer && (
                <div className="spe-transfer-note">
                  <strong>This is one leg of a credit transfer.</strong> Voiding it reverses the whole
                  transfer — <strong>both</strong> rows are voided together, and the credit returns to the
                  claim it was moved from. Voiding half a transfer would invent money, so it is not offered.
                </div>
              )}
            </>
          ) : (
            <>
              <div className="spe-grid">
                <label className="spe-field">
                  <span>Payment Date</span>
                  <input type="date" className="spe-input" value={form.payment_date} onChange={set('payment_date')} />
                </label>
                <label className="spe-field">
                  <span>Amount</span>
                  <input type="number" min="0.01" step="0.01" className="spe-input" value={form.amount} onChange={set('amount')} placeholder="0.00" />
                </label>
              </div>
              <div className="spe-field spe-field-full">
                <span>Payment Method</span>
                <div className="spe-pills">
                  {PAYMENT_METHODS.map((m) => (
                    <button
                      key={m.value}
                      type="button"
                      className={`spe-pill ${form.payment_method === m.value ? 'active' : ''}`}
                      onClick={() => setForm((f) => ({ ...f, payment_method: m.value }))}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              <label className="spe-field spe-field-full">
                <span>{REF_LABELS[form.payment_method] || 'Reference Number'}</span>
                <input type="text" className="spe-input" value={form.reference_number} onChange={set('reference_number')} placeholder="Optional" />
              </label>
              <label className="spe-field spe-field-full">
                <span>Notes</span>
                <textarea className="spe-textarea" rows={2} value={form.notes} onChange={set('notes')} placeholder="Optional" />
              </label>
            </>
          )}

          <label className="spe-field spe-field-full">
            <span>Reason for {isVoid ? 'void' : 'change'} <span className="spe-required">*</span></span>
            <textarea
              className="spe-textarea"
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={isVoid ? 'e.g. Payment recorded against the wrong claim' : 'e.g. Transposed digits while keying the amount'}
              autoFocus
            />
          </label>

          {overpayment > 0 && (
            <div className="spe-warning" role="alert">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span>
                This exceeds the remaining balance of {fmt(remainingExclThis)} by{' '}
                <strong>{fmt(overpayment)}</strong>. The excess will be recorded as a patient credit
                (refundable). You can still save it.
              </span>
            </div>
          )}

          {error && <div className="spe-error">{error}</div>}
        </div>

        <div className="spe-footer">
          <button className="spe-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className={`spe-btn-primary ${isVoid ? 'spe-btn-danger' : ''}`}
            onClick={handleSubmit}
            disabled={saving || !reason.trim()}
          >
            {saving ? 'Saving…' : isVoid ? (isTransfer ? 'Void Both Legs' : 'Void Payment') : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
